<template>
  <component
    :is="element"
    class="product-prices"
    :class="{
      [$class('loading')]: loading,
      'is-on-sale': formattedCompareAtPrice !== ''
    }"
  >
    <span
      v-if="varies"
      class="product-prices__varies"
      :class="classVaries ? classVaries : classPrice"
      v-text="$string('product.prices.from')"
    />

    <span
      v-if="formattedCompareAtPrice && !priceFirst"
      class="product-prices__compare-at-price"
      :class="classCompareAtPrice"
    >
      <span
        v-if="currencySymbolSmall"
        class="product-prices__currency"
        v-text="currencySymbol"
      />
      <span
        class="product-prices__value"
        v-text="formattedCompareAtPrice"
      />
    </span>

    <span
      v-if="prefix"
      class="product-prices__prefix"
      :class="classPrice"
      v-text="prefix"
    />

    <span
      class="product-prices__price"
      :class="[
        classPrice,
        { 'product-prices__price--on-sale': formattedCompareAtPrice },
      ]"
    >
      <span
        v-if="currencySymbolSmall"
        class="product-prices__currency"
        v-text="currencySymbol"
      />
      <span
        v-if="!varies"
        class="product-prices__value"
        v-text="formattedPrice"
      />

      <span
        v-else
        class="product-prices__value"
        v-text="formattedStartPrice"
      />
    </span>

    <span
      v-if="formattedCompareAtPrice && priceFirst"
      class="product-prices__compare-at-price"
      :class="classCompareAtPrice"
    >
      <span
        v-if="currencySymbolSmall"
        class="product-prices__currency"
        v-text="currencySymbol"
      />
      <span
        class="product-prices__value"
        v-text="formattedCompareAtPrice"
      />
    </span>

    <span
      v-if="available === false"
      class="product-prices__sold-out"
      :class="classPrice"
      v-text="$string('product.prices.sold_out')"
    />
  </component>
</template>

<script>

/**
 * Vue: Product prices (product-prices)
 * -----------------------------------------------------------------------------
 * Global product prices template.
 *
 * @param {Boolean} [available] - If the product is available.
 * @param {String} [classCompareAtPrice] - Comare at price text classes.
 * @param {String} [classPrice] - Price text classes.
 * @param {String} [classVaries] - 'From' text classes.
 * @param {Number|Boolean} compareAtPrice - Compare at price, in cents format.
 * @param {String} [element] - Custom element override.
 * @param {Boolean} [loading] - Loading state.
 * @param {Number} price - Product price, in cents format.
 * @param {Boolean} [priceFirst] - Display price before compare at price.
 * @param {Boolean} [varies] - Product variants vary in price.
 *
 */
import { formatNoDecimal } from '~/helpers/format'
export default {
  name: 'ProductPrices',

  props: {
    available: {
      type: Boolean,
      default: true,
    },
    classCompareAtPrice: {
      type: String,
      default: 'text-other-sale-s text-other-sale-l-desktop',
    },
    classPrice: {
      type: String,
      default: 'text-body-s-regular text-body-m-regular-desktop',
    },
    classVaries: {
      type: [Boolean, String],
      default: false,
    },
    compareAtPrice: {
      type: [Boolean, Number],
      default: false,
      required: true,
    },
    element: {
      type: String,
      default: 'p',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    startPrice: {
      type: [Boolean, Number],
      default: false,
      required: false,
    },
    price: {
      type: [Boolean, Number],
      default: 0,
      required: true,
    },
    priceFirst: {
      type: Boolean,
      default: true,
    },
    varies: {
      type: Boolean,
      default: false,
    },
    prefix: {
      type: String,
      default: '',
    },
    currencySymbolSmall: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      cnvsGlobal: true,
    }
  },

  computed: {

    /**
     * Compute compare at price template.
     * @returns {String}
     */
    formattedCompareAtPrice() {
      let price = ''
      if (this.compareAtPrice && this.price < this.compareAtPrice) {
        price = this.currencySymbolSmall
          ? formatNoDecimal(this.$formatMoney(this.compareAtPrice).replace(cnvs.store.currencySymbol, ''))
          : formatNoDecimal(this.$formatMoney(this.compareAtPrice))
      }

      return price
    },

    /**
     * Compute price template.
     * @returns {String}
     */
    formattedPrice() {
      let price = ''
      if (this.price === false) {
        return this.$string('accessibility.loading')
      }

      if (this.price <= 0) {
        return this.$string('product.prices.free')
      }

      if (this.price) {
        price = this.currencySymbolSmall
          ? formatNoDecimal(this.$formatMoney(this.price).replace(cnvs.store.currencySymbol, ''))
          : formatNoDecimal(this.$formatMoney(this.price))
      }
      return price
    },

    formattedStartPrice() {
      return this.currencySymbolSmall
        ? formatNoDecimal(this.$formatMoney(this.startPrice).replace(cnvs.store.currencySymbol, ''))
        : formatNoDecimal(this.$formatMoney(this.startPrice))
    },

    currencySymbol() {
      return cnvs.store.currencySymbol
    },
  },
}
</script>
